import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ContactUsService } from 'app/services/contact_us/contact.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss']
})
export class ContactComponent implements OnInit {
  btnDisable: boolean = false;
  contactFormGroup: FormGroup;
  isSubmit: boolean = false;
  isLoading: boolean = false;

  // Tracking validation states
  isValidatingTracking: boolean = false;
  trackingValidated: boolean = false;
  trackingValidationError: string | null = null;
  trackingVerifiedMsg: string | null = null;

  // Form submission feedback states
  formSuccessMsg: string | null = null;
  formErrorMsg: string | null = null;

  constructor(
    private _contactusService: ContactUsService,
    private toaster: ToastrService,
    private _router: Router
  ) { }

  ngOnInit(): void {
    this.contactFormGroup = new FormGroup({
      name: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      tracking_number: new FormControl('', Validators.required),
      subject: new FormControl('', Validators.required),
      message: new FormControl('', Validators.required),
      phone_number: new FormControl('')
    });

    // Reset tracking validation state on tracking input change
    this.contactFormGroup.get('tracking_number')?.valueChanges.subscribe(() => {
      this.trackingValidated = false;
      this.trackingValidationError = null;
      this.trackingVerifiedMsg = null;
    });
  }

  validateTrackingNumber(): void {
    const trackingNum = this.contactFormGroup.get('tracking_number')?.value;
    if (!trackingNum || !trackingNum.trim()) {
      this.trackingValidationError = 'Please enter a tracking number to verify.';
      this.trackingValidated = false;
      return;
    }

    this.isValidatingTracking = true;
    this.trackingValidationError = null;
    this.trackingVerifiedMsg = null;

    this._contactusService.validateTrackingNumber(trackingNum.trim()).subscribe({
      next: (res: any) => {
        this.isValidatingTracking = false;
        if (res && res.valid) {
          this.trackingValidated = true;
          this.trackingVerifiedMsg = 'Tracking number verified successfully!';
        } else {
          this.trackingValidated = false;
          this.trackingValidationError = res.message || 'Tracking number not found.';
        }
      },
      error: (err: any) => {
        this.isValidatingTracking = false;
        this.trackingValidated = false;
        this.trackingValidationError = err?.error?.message || 'Invalid tracking number. Please double check.';
      }
    });
  }

  submitForm(): void {
    if (this.btnDisable) { return; }

    this.isSubmit = true;
    this.formSuccessMsg = null;
    this.formErrorMsg = null;

    Object.keys(this.contactFormGroup.controls).forEach((key) => {
      this.contactFormGroup.get(key).markAsTouched();
    });

    if (this.contactFormGroup.invalid) {
      this.toaster.error('Please complete all required fields correctly.', '');
      return;
    }

    const formData = this.contactFormGroup.getRawValue();
    if (this.containsHtmlTags(formData)) {
      this.toaster.error('Invalid Content.', '');
      return;
    }

    if (!this.trackingValidated) {
      this.isValidatingTracking = true;
      this._contactusService.validateTrackingNumber(formData.tracking_number).subscribe({
        next: (res: any) => {
          this.isValidatingTracking = false;
          if (res && res.valid) {
            this.trackingValidated = true;
            this.proceedSubmit(formData);
          } else {
            this.trackingValidationError = res.message || 'Invalid tracking number.';
            this.toaster.error(this.trackingValidationError, '');
          }
        },
        error: (err: any) => {
          this.isValidatingTracking = false;
          this.trackingValidationError = err?.error?.message || 'Invalid tracking number. Please double check.';
          this.toaster.error(this.trackingValidationError, '');
        }
      });
      return;
    }

    this.proceedSubmit(formData);
  }

  private proceedSubmit(formData: any): void {
    this.btnDisable = true;
    this.isLoading = true;

    this._contactusService.createContact(formData).subscribe({
      next: (data: any) => {
        this.btnDisable = false;
        this.isLoading = false;
        this.formSuccessMsg = `Thank you! Your support ticket for tracking #${formData.tracking_number} has been submitted successfully.`;
        this.toaster.success(this.formSuccessMsg, '');
        this.contactFormGroup.reset();
        this.trackingValidated = false;
        this.trackingVerifiedMsg = null;
        this.isSubmit = false;
      },
      error: (err: any) => {
        this.btnDisable = false;
        this.isLoading = false;
        this.formErrorMsg = err?.error?.message || 'Something went wrong while submitting your inquiry.';
        this.toaster.error(this.formErrorMsg, '');
      }
    });
  }

  containsHtmlTags(formData: any): boolean {
    const regex = /<[^>]*>/;
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        const value = formData[key];
        if (typeof value === 'string' && regex.test(value)) {
          return true;
        }
      }
    }
    return false;
  }
}
